import { api } from '../lib/api';
import type {
  AssetAgeWarrantyReport,
  AssetQualityReport,
  CostSummary,
  EquipmentBySiteRow,
  ManagedReturnsReport,
} from '../types/api';

export interface OverviewReport {
  totalCustomers: number;
  assetsByStatus: { status: string; count: number }[];
  historicalOneOff: number;
  historicalManaged: number;
  historicalTotal: number;
  forecastRemainingManaged: number;
}

export async function getOverview(): Promise<OverviewReport> {
  const { data } = await api.get<OverviewReport>('/reporting/overview');
  return data;
}

// Commercially sensitive (Oakford's cost basis and margin) - MSP Admin
// only, both server-side (controller @Roles) and by never being called
// from any page a customer-side role can reach.
export interface ProfitByItem {
  name: string;
  revenue: number;
  cost: number;
  profit: number;
}

export interface ProfitAndLoss {
  customerOrganisationId: string;
  customerOrganisationName: string;
  // §18: revenueSoFar is what's actually been invoiced (ground truth);
  // revenueTotal adds everything still left to bill on active managed
  // items/ad-hoc contracts (§113) - the full expected value of what's
  // already committed. cost/profit/marginPercent are deliberately based on
  // revenueSoFar only, not revenueTotal - booking profit against revenue
  // that hasn't been billed yet would overstate margin on anything only
  // part-way through its term. `revenue` is kept as an alias of
  // revenueSoFar for back-compat with anything still reading it directly.
  revenueSoFar: number;
  revenueTotal: number;
  revenue: number;
  cost: number;
  profit: number;
  marginPercent: number;
  // §20: total of ISSUED credit notes already netted out of revenue above -
  // shown separately so it's clear why revenue is lower than gross invoiced.
  creditNoteTotal: number;
  missingCostRevenue: number;
  missingCostLineCount: number;
  byItem: ProfitByItem[];
}

export interface ProfitByCustomer {
  customerOrganisationId: string;
  name: string;
  revenueSoFar: number;
  revenueTotal: number;
  revenue: number;
  cost: number;
  profit: number;
  marginPercent: number;
}

export interface PlatformProfitOverview {
  revenueSoFar: number;
  revenueTotal: number;
  revenue: number;
  cost: number;
  profit: number;
  marginPercent: number;
  missingCostRevenue: number;
  byCustomer: ProfitByCustomer[];
}

export async function getProfitAndLoss(orgId: string): Promise<ProfitAndLoss> {
  const { data } = await api.get<ProfitAndLoss>(`/reporting/customer-organisations/${orgId}/profit-loss`);
  return data;
}

export async function getPlatformProfitOverview(): Promise<PlatformProfitOverview> {
  const { data } = await api.get<PlatformProfitOverview>('/reporting/profit-loss-overview');
  return data;
}

// Shape differs by role - see ReportingService.dashboardSummary on the
// backend. Discriminate on `scope` before reading the rest.
export interface MspDashboard {
  scope: 'MSP_ADMIN';
  pendingApprovals: number;
  ordersInFlight: number;
  pendingProcurementLines: number;
  deliverableLines: number;
  pendingLoanApprovals: number;
  overdueLoans: number;
  invoicesDueSoon: number;
}

export interface OrgAdminDashboard {
  scope: 'CUSTOMER_ADMIN';
  pendingApprovals: number;
  ordersInFlight: number;
  activeLoans: number;
  overdueLoans: number;
  nextInvoiceDate: string | null;
}

export interface MemberDashboard {
  scope: 'MEMBER';
  isSigningAuthority: boolean;
  pendingApprovals: number;
  pendingLoanApprovals: number;
  myOrdersPendingApproval: number;
  myOrdersInFlight: number;
  myActiveLoans: number;
  myOverdueLoans: number;
}

export type DashboardSummary = MspDashboard | OrgAdminDashboard | MemberDashboard;

export async function getDashboard(): Promise<DashboardSummary> {
  const { data } = await api.get<DashboardSummary>('/reporting/dashboard');
  return data;
}

export async function getEquipmentBySite(orgId: string): Promise<EquipmentBySiteRow[]> {
  const { data } = await api.get<EquipmentBySiteRow[]>(`/reporting/customer-organisations/${orgId}/equipment-by-site`);
  return data;
}

export async function getCostSummary(orgId: string): Promise<CostSummary> {
  const { data } = await api.get<CostSummary>(`/reporting/customer-organisations/${orgId}/cost-summary`);
  return data;
}

export async function getAssetAgeWarranty(orgId: string): Promise<AssetAgeWarrantyReport> {
  const { data } = await api.get<AssetAgeWarrantyReport>(`/reporting/customer-organisations/${orgId}/asset-age-warranty`);
  return data;
}

export async function getAssetQuality(orgId: string): Promise<AssetQualityReport> {
  const { data } = await api.get<AssetQualityReport>(`/reporting/customer-organisations/${orgId}/asset-quality`);
  return data;
}

export async function getManagedReturns(orgId: string, dueSoonDays?: number): Promise<ManagedReturnsReport> {
  const { data } = await api.get<ManagedReturnsReport>(`/reporting/customer-organisations/${orgId}/managed-returns`, {
    params: dueSoonDays ? { dueSoonDays } : undefined,
  });
  return data;
}
